import React, { useId, useRef } from 'react';
import './SegmentedTabs.css';

interface SegmentedTabsProps {
  /** Tablist accessible name. */
  label: string;
  /** Visible option labels, in order. */
  options: string[];
  selected: number;
  onSelect: (index: number) => void;
  /** Content for the selected option; rendered in the single shared panel. */
  children: React.ReactNode;
}

/** Narrow controlled tab control: one mounted panel, roving tabindex, arrow
   keys wrap, Home/End jump; rail scrolls itself, never the page. */
const SegmentedTabs: React.FC<SegmentedTabsProps> = ({ label, options, selected, onSelect, children }) => {
  const uid = useId();
  const panelId = `${uid}-panel`;
  const railRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const bringIntoRail = (i: number) => {
    const rail = railRef.current;
    const tab = tabRefs.current[i];
    if (!rail || !tab) return;
    const pad = 8;
    if (tab.offsetLeft < rail.scrollLeft + pad) {
      rail.scrollLeft = Math.max(0, tab.offsetLeft - pad);
    } else if (tab.offsetLeft + tab.offsetWidth > rail.scrollLeft + rail.clientWidth - pad) {
      rail.scrollLeft = tab.offsetLeft + tab.offsetWidth - rail.clientWidth + pad;
    }
  };

  const select = (i: number, focus = false) => {
    const next = (i + options.length) % options.length;
    onSelect(next);
    if (focus) tabRefs.current[next]?.focus({ preventScroll: true });
    bringIntoRail(next);
  };

  const onTabKeyDown = (e: React.KeyboardEvent, i: number) => {
    if (e.key === 'ArrowRight') { e.preventDefault(); select(i + 1, true); }
    else if (e.key === 'ArrowLeft') { e.preventDefault(); select(i - 1, true); }
    else if (e.key === 'Home') { e.preventDefault(); select(0, true); }
    else if (e.key === 'End') { e.preventDefault(); select(options.length - 1, true); }
  };

  return (
    <div className="st">
      <div className="st-rail" ref={railRef} role="tablist" aria-label={label} aria-orientation="horizontal">
        {options.map((opt, i) => (
          <button
            key={opt}
            ref={(el) => { tabRefs.current[i] = el; }}
            id={`${uid}-tab-${i}`}
            role="tab"
            type="button"
            className="st-tab"
            aria-selected={selected === i}
            aria-controls={panelId}
            tabIndex={selected === i ? 0 : -1}
            onClick={() => select(i)}
            onKeyDown={(e) => onTabKeyDown(e, i)}
            onFocus={() => onSelect(i)}
          >
            {opt}
          </button>
        ))}
      </div>
      <div
        id={panelId}
        role="tabpanel"
        aria-labelledby={`${uid}-tab-${selected}`}
        tabIndex={0}
        className="st-panel"
      >
        {children}
      </div>
    </div>
  );
};

export default SegmentedTabs;
